import type { Pokrycie } from './items-api';

/**
 * Z KTÓREJ MASZYNY MOŻNA ŚCIĄGNĄĆ PLIK — czysta reguła, bez Reacta i bez sieci.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ POWSTAŁO 07.09.2026 DLA NAPRAWY WIERSZY „portal nie ma tego pliku" (13 takich na testówce
 *  PESA). Tych wierszy nie da się naprawić wysłaniem — po naszej stronie nie ma ani jednego bajtu.
 *  Jedyna droga prowadzi w drugą stronę i zaczyna się od pytania: KTÓRA maszyna ten plik ma.
 *
 *  ⚠️⚠️ TYLKO MASZYNY ZE ZMIERZONYM PLIKIEM. Wypisanie wszystkich znaczyłoby, że człowiek wybiera
 *  węzeł, z którego nie ma czego ściągnąć — agent odrzuciłby zadanie, a w historii maszyny
 *  zostałby czerwony wpis za nic. `BRAK` i `NIEZMIERZONE` odpadają, i to jest ta sama reguła,
 *  po której ekran pokrycia decyduje, czy komórka jest klikalna.
 *
 *  ⚠️ `PUSTY_PLIK` TEŻ ODPADA, choć plik tam „jest": zerowa długość to na ekranie automatu puste
 *  miejsce. Ściągnięcie zera do biblioteki i rozesłanie go dalej rozniosłoby brak po całej flocie
 *  pod pozorem naprawy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type MaszynaZrodlowa = {
  agentId: string;
  nodeId: string;
  /** Nazwa do pokazania: automat, a gdy go nie ma — identyfikator węzła. */
  nazwa: string;
  /** Plik, który widzi TA maszyna — może różnić się od tego z mastera. */
  plik: string;
  rozmiar: number | null;
};

/** Stany, w których na maszynie realnie leży plik z treścią. */
const ZE_PLIKIEM = new Set(['MA', 'INNY_PLIK', 'INNY_ROZMIAR']);

export function maszynyZPlikiem(
  wiersz: Pokrycie['pokrycie'][number],
  maszyny: readonly Pokrycie['maszyny'][number][],
): MaszynaZrodlowa[] {
  const out: MaszynaZrodlowa[] = [];
  for (const m of maszyny) {
    const wpis = wiersz.maszyny.find((x) => x.agentId === m.agentId);
    if (wpis === undefined || !ZE_PLIKIEM.has(wpis.stan)) continue;
    /**
     * ⚠️ NAZWA PLIKU Z MASZYNY, A GDY JEJ NIE MA — Z MASTERA. `plikMaszyny` jest wypełniane tylko
     * przy rozjeździe (patrz schemat pokrycia), więc przy stanie `MA` trzeba wziąć tę z towaru.
     * Bez tego zapasu najczęstszy przypadek — maszyna ma dokładnie ten plik — nie miałby nazwy.
     */
    const plik = wpis.plikMaszyny ?? wiersz.towar.imageFile;
    if (plik === null || plik.trim() === '') continue;
    out.push({
      agentId: m.agentId,
      nodeId: m.nodeId,
      nazwa: m.automat?.nazwa ?? m.nodeId,
      plik,
      rozmiar: wpis.rozmiar,
    });
  }
  return out;
}

/**
 * Czy ten wiersz da się naprawić ściągnięciem z maszyny.
 *
 * ⚠️⚠️ WARUNEK JEST PODWÓJNY I OBA CZŁONY SĄ POTRZEBNE: portal NIE MA bajtów (inaczej naprawa to
 * zwykłe rozesłanie, które już istnieje) ORAZ któraś maszyna plik MA (inaczej nie ma skąd wziąć).
 * Przycisk pokazany bez drugiego członu prowadziłby do okna, które mówi „nie ma źródła" — czyli
 * do plakietki bez kroku, a to jest dokładnie to, czego się tu pozbywamy.
 */
export function mozliwaNaprawaZMaszyny(
  wiersz: Pokrycie['pokrycie'][number],
  maszyny: readonly Pokrycie['maszyny'][number][],
): boolean {
  if (wiersz.towar.imageFile === null || wiersz.towar.imageFile.trim() === '') return false;
  if (wiersz.wBibliotece) return false;
  return maszynyZPlikiem(wiersz, maszyny).length > 0;
}

/**
 * ILE PLIKÓW MA KTÓRA MASZYNA — do wyboru źródła przy zgrywaniu biblioteki.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZAMÓWIONE 07.09.2026 PO PRZYPADKU Z PESY: „maszyna, z której chciało pobrać, nie miała
 *  żadnych obrazków, a pozostałe automaty miały". Przyczyna była w API: zgrywanie brało PIERWSZY
 *  węzeł alfabetycznie (`wezly[0]`) i nie pytało, czy ten ma cokolwiek. Wybór bez tej liczby
 *  byłby wyborem w ciemno — czyli tym samym losowaniem, tylko wykonanym ręką człowieka.
 *
 *  ⚠️⚠️ „NIE SPRAWDZONO" TO NIE „ZERO PLIKÓW", i to jest tu najważniejsze rozróżnienie. Maszyna
 *  bez spisu nie ma zmierzonego ani jednego pliku — ale to znaczy, że NIE WIEMY, a nie że jest
 *  pusta. Pokazanie tam zera odesłałoby człowieka od jedynego automatu, który ma pełną bibliotekę.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type PlikiNaMaszynie = {
  agentId: string;
  nodeId: string;
  nazwa: string;
  /** Ile towarów ma na tej maszynie plik z treścią (`MA`, `INNY_PLIK`, `INNY_ROZMIAR`). */
  ma: number;
  /** Ile towarów ma tam brak albo pusty plik — to są zmierzone zera. */
  brak: number;
  /** Ile pozycji NIE ZMIERZONO. `ma + brak + niezmierzone` = liczba towarów z przypisanym plikiem. */
  niezmierzone: number;
  /** Kiedy ostatnio liczono spis; `null` = nigdy, więc liczby wyżej są tylko domysłem z braku danych. */
  spisAt: string | null;
};

export function plikiNaMaszynach(d: Pokrycie): PlikiNaMaszynie[] {
  return d.maszyny.map((m) => {
    let ma = 0;
    let brak = 0;
    let niezmierzone = 0;
    for (const p of d.pokrycie) {
      /** ⚠️ Towar bez przypisanego pliku nic nie mówi o maszynie — nie liczy się do żadnej kolumny. */
      if (p.towar.imageFile === null || p.towar.imageFile.trim() === '') continue;
      const stan = p.maszyny.find((x) => x.agentId === m.agentId)?.stan ?? 'NIEZMIERZONE';
      if (ZE_PLIKIEM.has(stan)) ma += 1;
      else if (stan === 'NIEZMIERZONE') niezmierzone += 1;
      else brak += 1;
    }
    return { agentId: m.agentId, nodeId: m.nodeId, nazwa: m.automat?.nazwa ?? m.nodeId, ma, brak, niezmierzone, spisAt: m.spisAt };
  });
}

/**
 * KTÓRĄ MASZYNĘ PODPOWIEDZIEĆ JAKO ŹRÓDŁO. `null` = nie ma z czego wybierać.
 *
 * ⚠️⚠️ NAJWIĘCEJ ZMIERZONYCH PLIKÓW, a nie „pierwsza alfabetycznie" — to właśnie ta druga reguła
 * wybrała na PESIE automat bez ani jednego obrazka. Przy remisie zostaje kolejność z instancji,
 * bo jest stała i przewidywalna.
 *
 * ⚠️ MASZYNA BEZ SPISU NIE JEST PODPOWIADANA, dopóki jakakolwiek inna ma zmierzone pliki: wybór
 * domyślny ma być tym, o którym coś WIEMY. Gdy nikt nie ma spisu, podpowiadamy pierwszą i mówimy
 * na ekranie, że nikt nie był sprawdzany.
 */
export function podpowiedzZrodla(maszyny: readonly PlikiNaMaszynie[]): PlikiNaMaszynie | null {
  if (maszyny.length === 0) return null;
  const zPlikami = maszyny.filter((m) => m.ma > 0);
  if (zPlikami.length === 0) return maszyny[0]!;
  return zPlikami.reduce((naj, m) => (m.ma > naj.ma ? m : naj), zPlikami[0]!);
}
